import { useIntl } from 'react-intl';

import { CardCTA } from '@/components/Cms/Card/components/CardCTA/CardCTA';
import { Heading } from '@/components/Cms/Heading/Heading';
import { Price } from '@/components/Price';
import { ProductDetail } from '@/components/ProductDetail';
import type { ContentfulCardFragment } from '@/graphql/contentful/api';
import { getTaxMessageShort } from '@/lib/regions';
import { getSubheadingType } from '@/lib/title';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import { parseSaleorProductSku } from '../../helpers';
import type { CardProps } from '../../types';
import { CardRichTextRenderer } from '../BaseCardType/RichTextEditor/CardRichTextRenderer';
import { CountDown } from '../CountDownType/CountDown';

import * as GS from '../../Card.styles';
import * as S from './CardContent.styles';

const getVariant = (productData: any) => {
  if (!productData.variant) return '';
  return productData.variant[0]?.node;
};

const getIsUsingSaleorData = (productData: any) => !!productData.variant;

interface FormatSubheadingProps {
  subHeading: string;
}

export const FormatSubheading = ({ subHeading }: FormatSubheadingProps) => {
  const text = subHeading ?? '';
  const boldedText = text.split(/<b>|<\/b>/).map((part, index) => {
    if (index % 2 === 1) {
      return (
        <S.Subheading
          key={`${part}${index}`}
          $boldText={true}
          data-test-id='FormatSubheadingTitle'
        >
          {part}
        </S.Subheading>
      );
    }

    return <S.Subheading key={`${part}${index}`}>{part}</S.Subheading>;
  });

  return <>{boldedText}</>;
};

export const CardContent = (props: CardProps) => {
  const {
    backgroundColor,
    cardText: text,
    contentOption,
    countdown,
    countdownToDatetime,
    placeholderHeading,
    saleorProductSku,
    showMore,
    subHeading,
    textColor,
    type,
    urlType,
  } = props;
  const { formatMessage } = useIntl();
  const { query } = useRegionProvider();
  const taxMessage = getTaxMessageShort(query.channel);

  const productData = parseSaleorProductSku(saleorProductSku);
  const variant = getVariant(productData);
  const isUsingSaleorData = getIsUsingSaleorData(productData);

  const heading = {
    ...placeholderHeading,
    text:
      (isUsingSaleorData
        ? `${variant?.product.watchName?.values[0]?.name ?? ''} `
        : '') + `${placeholderHeading?.text}`,
  };
  const isCountDownType =
    (type === 'HeroBanner' && !!countdown) || type === 'GenericCountdownCard';
  const shouldApplyHeadingBottomPadding =
    text === undefined && urlType === 'buttonDefault' && isCountDownType;

  const isShowPrice = isUsingSaleorData && variant;

  return (
    <>
      {text && contentOption === 'headingBelowText' && (
        <CardRichTextRenderer
          enableShowMore={showMore ?? false}
          text={text}
          textColor={textColor}
        />
      )}
      {placeholderHeading && (
        <S.HeadingWrapper $isShowPrice={isShowPrice} $type={type}>
          <Heading
            Heading={
              (heading as ContentfulCardFragment['placeholderHeading']) ??
              undefined
            }
            color={placeholderHeading?.mainColor ?? theme.colors.default}
            data-test-id='DefaultTypeCardPlaceholderHeading'
            isHeroSlider
            shouldApplyBottomPadding={shouldApplyHeadingBottomPadding}
          />
        </S.HeadingWrapper>
      )}
      {(subHeading || isUsingSaleorData) && (
        <GS.Subheader
          $isCountDown={isCountDownType}
          $textColor={textColor}
          $type={type}
          as={getSubheadingType(
            (heading.headingSeoTag ?? 'h2') as keyof HTMLElementTagNameMap,
          )}
          data-test-id='DefaultTypeCardSubheading'
        >
          {isShowPrice ? (
            <S.Pricing data-test-id='ProductVariantPricing'>
              <Price
                price={variant?.product.defaultVariant.pricing?.price?.gross}
                size='m'
              />
              <ProductDetail.PriceInformation
                data-test-id='PriceInformationBadge'
                loading={!variant}
              />
              <ProductDetail.TaxLabel data-test-id='CardProductVariantTaxLabel'>
                {taxMessage && formatMessage(taxMessage)}
              </ProductDetail.TaxLabel>
            </S.Pricing>
          ) : (
            <FormatSubheading subHeading={subHeading ?? ''} />
          )}
        </GS.Subheader>
      )}
      {!!countdown && isCountDownType && countdownToDatetime && (
        <CountDown
          countdownToDatetime={countdownToDatetime}
          textColor={textColor}
        />
      )}
      {text && contentOption !== 'headingBelowText' && (
        <CardRichTextRenderer
          backgroundColor={backgroundColor?.value}
          enableShowMore={showMore ?? false}
          isCountDown={isCountDownType}
          text={text}
          textColor={textColor}
          type={type}
        />
      )}
      {(urlType === 'button' ||
        urlType === 'buttonDefault' ||
        urlType === 'buttonOutlineBlue' ||
        urlType === 'buttonOutlineBlack' ||
        urlType === 'buttonOutlineWhite') && (
        <S.FlexGrowSpace data-test-id='BaseCardTypeFlexGrowSpace' />
      )}
      {/* CTA1 */}
      <CardCTA {...props} isCountDown={isCountDownType}></CardCTA>
    </>
  );
};
